import React, { useContext, useEffect } from "react";
import { RouteFilterContext } from "./context/RouteFilterContext";
import { RouteStatusContext } from "./context/RouteStatusContext";
import useCapabilityContext from "./context/useCapabilityContext";
import { useDeviceLayout } from "../common/context/DeviceLayoutContext";
import { getRouteStatusDataInCSVFormat } from "./utils/ExportUtils";
import { MobileDisplay } from "../common/MobileDisplay";
import Row from "@amzn/meridian/row";
import ExportCSV from "../ExportCSV/ExportCSV";
import { SelectOption } from "@amzn/meridian/select";
import Select from "@amzn/meridian/select";
import Link from "@amzn/meridian/link";
import RefreshTimer from "../RefreshTimer";
import { injectIntl } from "react-intl";
import Tag from "@amzn/meridian/tag";
import Column from "@amzn/meridian/column";
import { getFilterTagLabel } from "./utils/FilterUtil";
import { renderRouteSpeed } from "./utils/Util";
import { getRouteStatusList, PRODUCT_TYPES } from "./utils/ProductTypeUtils";
import { isDropToCartEnabled } from "./utils/EntityStatusManager";
import {
  CLEAR_ALL,
  CLUSTER,
  DELIVERY_SPEED,
  DRIVER_TYPE,
  LOCATION_TYPE,
  OVERFLOW,
  ROUTE_SPEED,
  ROUTE_STATUS,
  STAGE_BY_TIME,
  STATUS,
} from "./utils/Constants";
import {
  LOAD_FILTERS,
  REMOVE_ALL_FILTER,
  REMOVE_FILTER,
  SELECT_CLUSTER,
  SELECT_DEPARTURE,
  SELECT_DRIVER_TYPE,
  SELECT_PRODUCT_TYPE,
  SELECT_ROUTE_SPEED,
  SELECT_STAGE_LOCATION_TYPE,
  SELECT_STATUS,
} from "./utils/ReducerActionTypes";
import { STAGE_LOCATION_TYPE } from "../outboundAmzl/util/Constants";

const RouteFilter = (props) => {
  const { viewModelF, dispatchF } = useContext(RouteFilterContext);
  const { viewModel } = useContext(RouteStatusContext);
  const { configViewModelRef } = useCapabilityContext();
  const { spacingInset_shorthand2 } = useDeviceLayout();

  const routeStatusDataConversionHandler = () => {
    return getRouteStatusDataInCSVFormat(viewModel.routeList);
  };

  const isFilterSet = () => {
    let result = false;
    Object.entries(viewModelF).forEach((entry) => {
      const key = entry[0];
      const values = entry[1];
      result =
        result || (Array.isArray(values) && values.length && key !== "data");
    });
    return result;
  };

  const clearAllFilters = () => {
    dispatchF({ type: REMOVE_ALL_FILTER });
  };

  useEffect(() => {
    dispatchF({ type: LOAD_FILTERS });
  }, []);

  return (
    <Column spacingInset={spacingInset_shorthand2} spacing={"none"}>
      <MobileDisplay visibility={false}>
        <Row width="100%" spacingInset="none" alignmentHorizontal={"right"}>
          <ExportCSV
            isLink={true}
            csvDataProvider={routeStatusDataConversionHandler}
            filePrefix={ROUTE_STATUS}
          />
        </Row>
      </MobileDisplay>

      <Row
        wrap={"down"}
        width="100%"
        spacingInset="medium small"
        spacing="small"
        backgroundColor="secondary"
      >
        <Select
          onChange={(data) =>
            dispatchF({ type: SELECT_PRODUCT_TYPE, productType: data })
          }
          value={viewModelF.productType}
          size="small"
          placeholder={props.intl.formatMessage({
            id: "routeStatusFilter.productType",
            defaultMessage: PRODUCT_TYPES.ALL_ROUTES,
          })}
          width={"145px"}
        >
          {Object.values(PRODUCT_TYPES).map((type) => (
            <SelectOption key={type} label={type} value={type} />
          ))}
        </Select>
        <Select
          onChange={(data) =>
            dispatchF({ type: SELECT_DEPARTURE, departure: data })
          }
          value={viewModelF.departure}
          size="small"
          placeholder={props.intl.formatMessage({
            id: "routeStatusFilter.stageByTime",
            defaultMessage: STAGE_BY_TIME,
          })}
          width={"145px"}
        >
          {[...viewModelF.data.departure].sort().map((departure) => (
            <SelectOption
              key={departure}
              label={"Stage By " + departure}
              value={departure}
            />
          ))}
        </Select>

        <Select
          onChange={(data) => dispatchF({ type: SELECT_STATUS, status: data })}
          value={viewModelF.status}
          size="small"
          placeholder={props.intl.formatMessage({
            id: "cartInfo.status",
            defaultMessage: STATUS,
          })}
          width={"120px"}
        >
          {getRouteStatusList(
            viewModelF.productType,
            isDropToCartEnabled(configViewModelRef.current.stationConfig),
          ).map((status) => (
            <SelectOption key={status} label={status} value={status} />
          ))}
        </Select>

        <Select
          onChange={(data) =>
            dispatchF({ type: SELECT_CLUSTER, cluster: data })
          }
          value={viewModelF.cluster}
          size="small"
          placeholder={props.intl.formatMessage({
            id: "routeStatusFilter.cluster",
            defaultMessage: CLUSTER,
          })}
          width={"115px"}
        >
          {[...viewModelF.data.cluster].sort().map((cluster) => (
            <SelectOption key={cluster} label={cluster} value={cluster} />
          ))}
        </Select>
        <Select
          onChange={(data) =>
            dispatchF({
              type: SELECT_STAGE_LOCATION_TYPE,
              stageLocationType: data,
            })
          }
          value={viewModelF.stageLocationType}
          size="small"
          placeholder={props.intl.formatMessage({
            id: "cartInfo.stageLocationType",
            defaultMessage: LOCATION_TYPE,
          })}
          width={"137px"}
        >
          <SelectOption
            key={STAGE_LOCATION_TYPE.OVERFLOW}
            label={props.intl.formatMessage({
              id: "cartInfo.overflowStatus",
              defaultMessage: OVERFLOW,
            })}
            value={STAGE_LOCATION_TYPE.OVERFLOW}
          />
        </Select>
        <Select
          onChange={(data) =>
            dispatchF({ type: SELECT_ROUTE_SPEED, deliverySpeed: data })
          }
          value={viewModelF.deliverySpeed}
          size="small"
          placeholder={props.intl.formatMessage({
            id: "route.deliverySpeed",
            defaultMessage: DELIVERY_SPEED,
          })}
          width={"145px"}
        >
          {Object.values(ROUTE_SPEED).map((type) => (
            <SelectOption
              key={type}
              label={renderRouteSpeed(type)}
              value={type}
            />
          ))}
        </Select>
        <Select
          onChange={(data) =>
            dispatchF({ type: SELECT_DRIVER_TYPE, driverType: data })
          }
          value={viewModelF.driverType}
          size="small"
          placeholder={props.intl.formatMessage({
            id: "routeStatusFilter.driverType",
            defaultMessage: DRIVER_TYPE,
          })}
          width={"125px"}
        >
          {[...viewModelF.data.driverType].sort().map((driverType) => (
            <SelectOption
              key={driverType}
              label={driverType}
              value={driverType}
            />
          ))}
        </Select>
      </Row>
      <Row width="100%" spacingInset="small none none none">
        <Row spacing={"small"}>
          {Object.entries(viewModelF).map((entry) => {
            const key = entry[0];
            const values = entry[1];
            return Array.isArray(values) && key !== "data"
              ? values.map((value) => {
                  return (
                    <Tag
                      key={value}
                      type="theme"
                      onClose={() =>
                        dispatchF({
                          type: REMOVE_FILTER,
                          data: { key, value },
                        })
                      }
                    >
                      {getFilterTagLabel(key, value)}
                    </Tag>
                  );
                })
              : null;
          })}
          {isFilterSet() ? (
            <Link onClick={clearAllFilters} type="secondary">
              {" "}
              {CLEAR_ALL}{" "}
            </Link>
          ) : null}
        </Row>
      </Row>

      <Row spacingInset="small none">
        <RefreshTimer
          deps={[viewModel.lastUpdated]}
          lastUpdated={viewModel.lastUpdated}
        />
      </Row>
    </Column>
  );
};

export default injectIntl(RouteFilter);



// WEBPACK FOOTER //
// ./src/components/RouteStatus/RouteFilter.js